/*
This is an error handler for failed requests to the backend.
It handles the error code and returns an error message to the caller function (see fetchWrapper).
*/

import type { ResponseInterface } from "~~/api/auth/interfaces";
import type {
  ErrorBodyInterface,
  ErrorHandlerInterface,
} from "~~/interfaces/errorHandler";
import { useAuthStore } from "~~/store/auth";

export const errorHandler = async (
  response: ResponseInterface,
  t: (key: string, params?: Record<string, unknown>) => string,
) => {
  const error: ErrorHandlerInterface = {
    message: "",
    code: response?.status,
    skipToast: false,
  };

  if (response == null) {
    error.code = -1;
    error.message = t("error.connection");
  } else {
    const rawBody = response?._data as unknown;
    if (rawBody) {
      if (typeof rawBody === "string") {
        error.message = rawBody;
      } else if (typeof rawBody === "object") {
        const body = rawBody as Record<string, unknown>;

        if (Array.isArray(body.errors) && body.errors.length > 0) {
          const firstError = body.errors[0];
          if (
            firstError &&
            typeof firstError === "object" &&
            "detail" in firstError
          ) {
            const detail = firstError.detail;
            if (Array.isArray(detail)) {
              error.message = detail.join(", ");
            } else {
              error.message = String(detail);
            }
          }
        }
        // Check for simple detail field
        else if (body.detail && typeof body.detail === "string") {
          error.message = body.detail;
        }
        // Check for message field
        else if (body.message && typeof body.message === "string") {
          error.message = body.message;
        }
        // Check for error field
        else if (body.error && typeof body.error === "string") {
          error.message = body.error;
        }
      }
    }

    if (error.code === 208) {
      error.skipToast = true;
    } else if (error.code === 400) {
      // Use backend message if available, otherwise fallback to generic
      if (!error.message) {
        error.message = t("error.bad_request");
      }
    } else if (error.code === 401) {
      const { refreshToken } = useAuthStore();
      const isTokenRefreshed = await refreshToken();
      if (!isTokenRefreshed) {
        error.message = t("error.session_expired");
        const localePath = useLocalePath();
        await navigateTo(localePath("/login"));
      } else {
        error.message = "";
        error.skipToast = true;
      }
    } else if (error.code === 403) {
      error.message = t("error.forbidden");
    } else if (error.code === 404) {
      error.message = t("error.not_found");
    } else if (error.code === 429) {
      error.message = parseErrorMessage(
        (response?._data as ErrorBodyInterface) ?? t("error.too_many_requests"),
      );
    } else if (error.code === 500) {
      error.message = parseErrorMessage(
        (response?._data as ErrorBodyInterface) ?? t("error.internal"),
      );
    } else if (error.code === 502) {
      error.message = t("error.bad_gateway");
    }
  }
  if (!error.message && !error.skipToast) {
    console.error(response);
    error.message = t("error.unhandled", { code: error.code });
  }
  return error;
};

function parseErrorMessage(errorBody: ErrorBodyInterface): string {
  // Check for errors array first (common Django REST format)
  const bodyWithErrors = errorBody as Record<string, unknown>;
  if (
    Array.isArray(bodyWithErrors.errors) &&
    bodyWithErrors.errors.length > 0
  ) {
    const firstError = bodyWithErrors.errors[0];
    if (
      firstError &&
      typeof firstError === "object" &&
      "detail" in firstError
    ) {
      return String((firstError as Record<string, unknown>).detail);
    }
  }

  // Check for message field
  if (Array.isArray(errorBody.message) && errorBody.message.length > 0) {
    if (typeof errorBody.message[0] === "string")
      return errorBody.message[0] as string;
    const errorEntries = Object.entries(errorBody.message[0]);
    let errorMessage = "";
    errorEntries.forEach((error) => {
      // Don't include arrays/objects directly
      if (typeof error[1] === "string" || typeof error[1] === "number") {
        errorMessage += `${error[0]}: ${error[1]}\n`;
      }
    });
    return errorMessage || "Errore di validazione";
  } else if (errorBody.message) {
    return errorBody.message as string;
  } else {
    // Last resort: iterate over body entries, but skip complex objects
    const errorEntries = Object.entries(errorBody);
    let errorMessage = "";
    errorEntries.forEach((error) => {
      // Skip arrays and objects, only include primitive values
      if (typeof error[1] === "string" || typeof error[1] === "number") {
        errorMessage += `${error[0]}: ${error[1]}\n`;
      }
    });
    return errorMessage || "Errore sconosciuto";
  }
}
